import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import type { InspectionFinding, FindingSeverity } from '@/lib/types';

const severityColors: Record<FindingSeverity, string> = {
  low: 'bg-yellow-100 text-yellow-800',
  medium: 'bg-orange-100 text-orange-800',
  high: 'bg-red-100 text-red-800',
};

const severityLabels: Record<FindingSeverity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

interface FindingsListProps {
  findings: InspectionFinding[];
  emptyMessage?: string;
}

export function FindingsList({ findings, emptyMessage = 'No findings recorded' }: FindingsListProps) {
  if (findings.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {findings.map((finding, index) => (
        <Card key={finding.id || index} className="border-l-4" style={{ borderLeftColor: getSeverityBorderColor(finding.severity) }}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className={severityColors[finding.severity]}>
                    {severityLabels[finding.severity]}
                  </Badge>
                  {finding.location && (
                    <span className="text-sm text-gray-500">
                      Location: {finding.location}
                    </span>
                  )}
                </div>
                <p className="text-gray-700">{finding.description}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function getSeverityBorderColor(severity: FindingSeverity): string {
  switch (severity) {
    case 'high':
      return '#ef4444';
    case 'medium':
      return '#f97316';
    case 'low':
      return '#eab308';
    default:
      return '#d1d5db';
  }
}
